import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  FormCard, FormCardHeader, Line, SubmitButton, StyledA, FormCardTransition, FormCardVariants
} from "styles/styled";
import {RootState} from 'store';
import styled from "styled-components";
import { $Action, $Caution, $Warning, Colors, Spacing } from "styles/constants";
import SuccessIcon from "../../assets/svg/success-icon";
import WarningIcon from "../../assets/svg/warning-icon";
import ErrorIcon from "../../assets/svg/error-icon";
import { motion } from "framer-motion";
import useDocumentTitle from "../../util/hooks/useDocumentTitle";
import AppSpinner from "../../components/spinner/spinner";

const ActionText = styled.h1 `{
  font-size: 36px;
  line-height: 36px;
  font-weight: 600;
  color: ${Colors.SlateDark};
  margin-bottom: ${Spacing.Space4};
  text-align: center;
}`;

const TextContainer = styled.div `{
  text-align: center;
  width: 100%;
  color: ${Colors.SlateDark};
  margin: ${Spacing.Space4} auto;
}`;

const SecondButton = styled(SubmitButton)`
  margin-top: 16px;
`;

const fallbackComponent = (
  <motion.div
    initial="out"
    animate="in"
    exit="out"
    transition={FormCardTransition}
    variants={FormCardVariants}
  >
    <FormCard>
      <ActionText>Uh oh, something went wrong.</ActionText>
      <WarningIcon color={$Warning} size={'55'}/>
      <TextContainer>
        <p>If it seems like something should be here, <StyledA aria-label="email support" href="mailto:support@bitpay.com">please let us know</StyledA>.</p>
      </TextContainer>
      <SubmitButton type="button" role="button" aria-label="close"
                    onClick={() => window.location.assign('/')}>Back Home</SubmitButton>
    </FormCard>
  </motion.div>
);

const Alert = () => {
  const [icon, setIcon] = useState<JSX.Element | undefined>()
  const alertComponent = useSelector((state: RootState) => state.app.instructions?.context && state.app.instructions.context.alertComponent);
  const [defaultComponent, setDefaultComponent] = useState(<AppSpinner/>)
  useDocumentTitle(alertComponent?.title || 'BitPay');

  useEffect(() => {
    switch (alertComponent?.icon) {
      case 'success':
        setIcon(<SuccessIcon color={$Action} size={'55'} />);
        break;
      case 'warning':
        setIcon(<WarningIcon color={$Warning} size={'55'} />);
        break;
      case 'error':
        setIcon(<ErrorIcon color={$Caution} size={'55'} />);
        break;
    }
  }, [alertComponent])

  useEffect(() => {
    setTimeout(() => {
      setDefaultComponent(fallbackComponent)
    }, 5000)
  }, [])

  const handleClick = () => {
    alertComponent?.buttonRedirect && window.location.assign(alertComponent.buttonRedirect);
  };

  const handleClick2 = () => {
    alertComponent?.button2Redirect && window.location.assign(alertComponent.button2Redirect);
  };

  return (
    <>
      {alertComponent ?
        <motion.div
          initial="out"
          animate="in"
          exit="out"
          transition={FormCardTransition}
          variants={FormCardVariants}
        >
          <FormCard>
            {alertComponent?.heading ? <><FormCardHeader>{alertComponent?.heading}</FormCardHeader><Line/></> : null}
            <ActionText>{alertComponent?.subheading}</ActionText>
            {icon}
            <TextContainer>
              <p>{alertComponent?.copy}</p>
              {alertComponent?.support &&
              <p>Please contact <StyledA aria-label="email support" href="mailto:support@bitpay.com">support</StyledA> if
                you're having trouble.</p>
              }
            </TextContainer>
            {alertComponent?.buttonRedirect && alertComponent?.buttonCopy &&
            <SubmitButton type="button" role="button" aria-label="close"
                          onClick={handleClick}>{alertComponent.buttonCopy}</SubmitButton>}
            {alertComponent?.button2Redirect && alertComponent?.button2Copy &&
            <SecondButton
              type="button"
              role="button"
              aria-label="close"
              primary={alertComponent?.button2Type === 'primary'}
              secondary={alertComponent?.button2Type === 'secondary'}
              tertiary={alertComponent?.button2Type === 'tertiary'}
              onClick={handleClick2}
            >{alertComponent.button2Copy}</SecondButton>}
          </FormCard>
        </motion.div>
        :
        defaultComponent
      }
    </>
  );
};

export default Alert;


